import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { usePopper } from 'react-popper'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { Loader } from 'shared/components/loader'
import { usePlainClickOutside } from 'shared/hooks/use-click-outside'
import SearchIcon from 'shared/icons/search-icon'
import { useCommunitySearchSuggestions } from 'modules/community/hooks/use-community-search-suggestions'
import { buildSearchUrl } from './utils'

interface FormData {
  search: string
}

interface Props {
  className?: string
  placeholder?: string
}

export function CommunitySearchForm({ className, placeholder }: Props) {
  const router = useRouter()
  const { t } = useTranslation()
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({ defaultValues: { search: '' } })
  const [isOpen, setIsOpen] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 15] },
      },
    ],
  })

  const { communityPath, topicPath } = router.query as { communityPath: string; topicPath?: string }

  usePlainClickOutside(formRef, () => setIsOpen(false))

  const { suggestions, isLoading, init } = useCommunitySearchSuggestions({
    query: isValid ? watch('search') : undefined,
  })

  const onSubmit = ({ search }: FormData) => {
    router.push(buildSearchUrl(search, communityPath, topicPath))
  }

  const error = errors.search?.message

  useEffect(() => {
    if (router.asPath.includes('/search') && router.query.q) {
      setValue('search', router.query.q as string)
    }
  }, [router.asPath, router.query.q, setValue])

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      className={twMerge('flex flex-col gap-3 rounded-md bg-background-secondary p-5', className)}
      onMouseEnter={init}
    >
      <div className="relative" ref={setReferenceElement}>
        <input
          // onFocus={() => setIsOpen(true)}
          type="search"
          placeholder={placeholder ?? t('global.search')}
          className="hide-webkit-search-cancel-button max-h-12 w-full rounded-md border border-text-secondary/30 p-4 pr-9 text-text-primary focus:border-brand-primary focus:outline-none lg:max-h-10"
          {...register('search', {
            minLength: { value: 3, message: 'community.search.errors.min_length' },
          })}
        />
        <button
          type="submit"
          title={t('global.search')}
          className="absolute right-4 top-1/2 -translate-y-1/2"
        >
          <SearchIcon className="size-5 text-text-primary" />
        </button>
      </div>
      {!!error && <p className="text-sm text-danger">{t(error)}</p>}
      {isOpen && (
        <div
          ref={setPopperElement}
          className="z-30"
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <div className="min-w-60 rounded-lg bg-background-secondary py-5 shadow-[0px_4px_10px_0px_#00000040]">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader />
              </div>
            ) : (
              <>
                {!suggestions?.length ? (
                  <p className="flex justify-center py-2 text-base">
                    {t('global.list.empty_list')}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {suggestions.map(suggestion => (
                      <li key={suggestion.id} className="w-full">
                        <Link
                          href={buildSearchUrl(suggestion.value, communityPath, topicPath)}
                          className="inline-block w-full cursor-pointer pl-5 hover:bg-text-tertiary"
                        >
                          {suggestion.value}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </form>
  )
}
